/**
 * TenantAdminInvitePage - Self-contained Tenant Admin Invite Tool
 *
 * This is a standalone, modular page for sending tenant admin invitations.
 * It is intentionally decoupled from the main app's architecture for easy removal.
 *
 * TO REMOVE THIS MODULE:
 * 1. Delete the entire `src/pages/AdminTools` directory
 * 2. Remove the route from App.tsx: <Route path="/admin-tools/invite" ... />
 * 3. (Optional) Remove any navigation links to this page
 *
 * Dependencies: Only React, MUI, lucide-react icons, and environment config
 */

import React, { useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  InputAdornment,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Link,
} from "@mui/material";
import {
  Mail,
  Building2,
  Send,
  Shield,
  CheckCircle,
  Copy,
  ExternalLink,
  Key,
  User,
  Phone,
  Globe,
  FileText,
  Calendar,
  Users,
  BookOpen,
  Lock,
  RefreshCw,
} from "lucide-react";

// Import environment config from main app
import { currentConfig } from "../../config/environment";

// ============================================================================
// CONFIGURATION
// ============================================================================

const CONFIG = {
  // Use the same API base URL as the main app
  API_BASE_URL: currentConfig.apiBaseUrl,

  // Hard-coded password for prototype access (this page is for testing only)
  ACCESS_PASSWORD: "Password@12345",

  // Default license settings for new tenants (used when fields are left empty)
  DEFAULT_LICENSE: {
    maxUsers: 10,
    maxStudies: 5,
    validityDays: 365, // License validity in days
  },
};

// ============================================================================
// TYPES
// ============================================================================

interface LicenseDetails {
  licenseKey: string;
  expirationDate: string;
  maxUsers: number | string;
  maxStudies: number | string;
}

interface TenantDetails {
  name: string;
  description?: string;
  logoUrl?: string;
  country?: string;
  deploymentType: "On-Prem" | "Centrally Hosted";
  contactEmail: string;
  contactPhone?: string;
  licenseDetails?: LicenseDetails;
}

interface InviteFormData {
  email: string;
  inviteeName: string;
  tenantDetails: TenantDetails;
}

interface InviteResponse {
  id: string;
  email: string;
  token: string;
  status: string;
  expiresAt: string;
  createdAt: string;
}

interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const generateLicenseKey = (): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  const segments = 4;
  const segmentLength = 4;
  const parts: string[] = [];

  for (let i = 0; i < segments; i++) {
    let segment = "";
    for (let j = 0; j < segmentLength; j++) {
      segment += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    parts.push(segment);
  }

  return parts.join("-");
};

const getExpirationDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString();
};

// ============================================================================
// PASSWORD WALL COMPONENT (Simple hard-coded authentication for prototyping)
// ============================================================================

interface PasswordWallProps {
  onUnlock: () => void;
}

const PasswordWall: React.FC<PasswordWallProps> = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password === CONFIG.ACCESS_PASSWORD) {
      onUnlock();
    } else {
      setError("Incorrect password");
      setPassword("");
    }
  };

  return (
    <Card sx={{ maxWidth: 400, mx: "auto", mt: 4 }}>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Shield size={24} />
            <Typography variant="h6">Access Required</Typography>
          </Box>
        }
        subheader="Enter password to access the tenant admin invite tool"
      />
      <CardContent>
        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Lock size={20} />
                </InputAdornment>
              ),
            }}
          />
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3 }}>
            Unlock
          </Button>
        </form>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 2 }}
        >
          This is a prototype tool for testing purposes only.
        </Typography>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// INVITE FORM COMPONENT
// ============================================================================

interface InviteFormProps {
  onSuccess: (response: InviteResponse) => void;
}

const InviteForm: React.FC<InviteFormProps> = ({ onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState<InviteFormData>({
    email: "",
    inviteeName: "",
    tenantDetails: {
      name: "",
      description: "",
      logoUrl: "",
      country: "",
      deploymentType: "Centrally Hosted",
      contactEmail: "",
      contactPhone: "",
      licenseDetails: {
        licenseKey: generateLicenseKey(),
        expirationDate: "",
        maxUsers: "",
        maxStudies: "",
      },
    },
  });

  const handleInputChange = (field: string, value: string | number) => {
    if (field.startsWith("tenantDetails.")) {
      const tenantField = field.replace("tenantDetails.", "");
      if (tenantField.startsWith("licenseDetails.")) {
        const licenseField = tenantField.replace("licenseDetails.", "");
        setFormData((prev) => ({
          ...prev,
          tenantDetails: {
            ...prev.tenantDetails,
            licenseDetails: {
              licenseKey:
                licenseField === "licenseKey"
                  ? String(value)
                  : (prev.tenantDetails.licenseDetails?.licenseKey ?? ""),
              expirationDate:
                licenseField === "expirationDate"
                  ? String(value)
                  : (prev.tenantDetails.licenseDetails?.expirationDate ?? ""),
              maxUsers:
                licenseField === "maxUsers"
                  ? value
                  : (prev.tenantDetails.licenseDetails?.maxUsers ?? ""),
              maxStudies:
                licenseField === "maxStudies"
                  ? value
                  : (prev.tenantDetails.licenseDetails?.maxStudies ?? ""),
            },
          },
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          tenantDetails: {
            ...prev.tenantDetails,
            [tenantField]: value,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const regenerateLicenseKey = () => {
    handleInputChange(
      "tenantDetails.licenseDetails.licenseKey",
      generateLicenseKey(),
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Prepare license details with defaults for empty fields
      const licenseDetails = {
        licenseKey:
          formData.tenantDetails.licenseDetails?.licenseKey ??
          generateLicenseKey(),
        expirationDate:
          formData.tenantDetails.licenseDetails?.expirationDate ??
          getExpirationDate(CONFIG.DEFAULT_LICENSE.validityDays),
        maxUsers:
          formData.tenantDetails.licenseDetails?.maxUsers !== "" &&
          formData.tenantDetails.licenseDetails?.maxUsers !== undefined
            ? Number(formData.tenantDetails.licenseDetails.maxUsers)
            : CONFIG.DEFAULT_LICENSE.maxUsers,
        maxStudies:
          formData.tenantDetails.licenseDetails?.maxStudies !== "" &&
          formData.tenantDetails.licenseDetails?.maxStudies !== undefined
            ? Number(formData.tenantDetails.licenseDetails.maxStudies)
            : CONFIG.DEFAULT_LICENSE.maxStudies,
      };

      // Prepare the request payload
      const payload = {
        email: formData.email,
        invite_metadata: {
          inviteeName: formData.inviteeName.trim() || undefined,
          tenantDetails: {
            name: formData.tenantDetails.name,
            description: formData.tenantDetails.description ?? undefined,
            logoUrl: formData.tenantDetails.logoUrl ?? undefined,
            country: formData.tenantDetails.country ?? undefined,
            deploymentType: formData.tenantDetails.deploymentType,
            contactEmail: formData.tenantDetails.contactEmail,
            contactPhone: formData.tenantDetails.contactPhone ?? undefined,
            licenseDetails,
          },
        },
      };

      const response = await fetch(`${CONFIG.API_BASE_URL}/invites/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result: ApiResponse<InviteResponse> = await response.json();

      if (!response.ok) {
        throw new Error(result.message ?? "Failed to create invite");
      }

      onSuccess(result.data ?? result);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create invite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void handleSubmit(e);
      }}
    >
      {/* Invite Email */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Mail size={20} />
          Invitation Email
        </Typography>
        <TextField
          fullWidth
          label="Email Address"
          type="email"
          value={formData.email}
          onChange={(e) => handleInputChange("email", e.target.value)}
          required
          helperText="The email address of the new tenant administrator"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Mail size={18} />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth
          label="Full Name"
          value={formData.inviteeName}
          onChange={(e) => handleInputChange("inviteeName", e.target.value)}
          helperText="Full name of the tenant administrator (optional, shown during registration)"
          sx={{ mt: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <User size={18} />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Tenant Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Building2 size={20} />
          Tenant Organization Details
        </Typography>

        <Box sx={{ display: "grid", gap: 2 }}>
          <TextField
            fullWidth
            label="Organization Name"
            value={formData.tenantDetails.name}
            onChange={(e) =>
              handleInputChange("tenantDetails.name", e.target.value)
            }
            required
            helperText="Name of the new tenant organization"
          />

          <TextField
            fullWidth
            label="Contact Email"
            type="email"
            value={formData.tenantDetails.contactEmail}
            onChange={(e) =>
              handleInputChange("tenantDetails.contactEmail", e.target.value)
            }
            required
            helperText="Primary contact email for the organization"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Mail size={18} />
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            label="Description"
            value={formData.tenantDetails.description}
            onChange={(e) =>
              handleInputChange("tenantDetails.description", e.target.value)
            }
            multiline
            rows={2}
            helperText="Optional description of the organization"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FileText size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              fullWidth
              label="Country"
              value={formData.tenantDetails.country}
              onChange={(e) =>
                handleInputChange("tenantDetails.country", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Globe size={18} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Contact Phone"
              value={formData.tenantDetails.contactPhone}
              onChange={(e) =>
                handleInputChange("tenantDetails.contactPhone", e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Phone size={18} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <FormControl fullWidth>
            <InputLabel>Deployment Type</InputLabel>
            <Select
              value={formData.tenantDetails.deploymentType}
              label="Deployment Type"
              onChange={(e) =>
                handleInputChange(
                  "tenantDetails.deploymentType",
                  e.target.value,
                )
              }
            >
              <MenuItem value="Centrally Hosted">Centrally Hosted</MenuItem>
              <MenuItem value="On-Prem">On-Prem</MenuItem>
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Logo URL"
            value={formData.tenantDetails.logoUrl}
            onChange={(e) =>
              handleInputChange("tenantDetails.logoUrl", e.target.value)
            }
            helperText="Optional URL to organization logo"
          />
        </Box>
      </Paper>

      {/* License Details */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography
          variant="h6"
          sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
        >
          <Key size={20} />
          License Configuration
          <Chip size="small" label="Optional" sx={{ ml: 1 }} />
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Leave fields empty to use default values (Max Users:{" "}
          {CONFIG.DEFAULT_LICENSE.maxUsers}, Max Studies:{" "}
          {CONFIG.DEFAULT_LICENSE.maxStudies}, Validity:{" "}
          {CONFIG.DEFAULT_LICENSE.validityDays} days)
        </Typography>

        <Box sx={{ display: "grid", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              label="License Key"
              value={formData.tenantDetails.licenseDetails?.licenseKey}
              onChange={(e) =>
                handleInputChange(
                  "tenantDetails.licenseDetails.licenseKey",
                  e.target.value,
                )
              }
              helperText="Auto-generated license key (can be customized)"
            />
            <IconButton
              onClick={regenerateLicenseKey}
              sx={{ mt: 1 }}
              title="Regenerate"
            >
              <RefreshCw size={20} />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Expiration Date"
            type="datetime-local"
            value={
              formData.tenantDetails.licenseDetails?.expirationDate?.slice(
                0,
                16,
              ) ?? ""
            }
            onChange={(e) =>
              handleInputChange(
                "tenantDetails.licenseDetails.expirationDate",
                e.target.value ? new Date(e.target.value).toISOString() : "",
              )
            }
            InputLabelProps={{ shrink: true }}
            helperText={`Leave empty for default: ${CONFIG.DEFAULT_LICENSE.validityDays} days from now`}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Calendar size={18} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <TextField
              fullWidth
              label="Max Users"
              type="number"
              value={formData.tenantDetails.licenseDetails?.maxUsers}
              onChange={(e) =>
                handleInputChange(
                  "tenantDetails.licenseDetails.maxUsers",
                  e.target.value,
                )
              }
              placeholder={String(CONFIG.DEFAULT_LICENSE.maxUsers)}
              helperText={`Default: ${CONFIG.DEFAULT_LICENSE.maxUsers}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Users size={18} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Max Studies"
              type="number"
              value={formData.tenantDetails.licenseDetails?.maxStudies}
              onChange={(e) =>
                handleInputChange(
                  "tenantDetails.licenseDetails.maxStudies",
                  e.target.value,
                )
              }
              placeholder={String(CONFIG.DEFAULT_LICENSE.maxStudies)}
              helperText={`Default: ${CONFIG.DEFAULT_LICENSE.maxStudies}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <BookOpen size={18} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : <Send />}
      >
        {loading ? "Sending Invite..." : "Send Tenant Admin Invite"}
      </Button>
    </form>
  );
};

// ============================================================================
// SUCCESS DISPLAY COMPONENT
// ============================================================================

interface SuccessDisplayProps {
  invite: InviteResponse;
  onReset: () => void;
}

const SuccessDisplay: React.FC<SuccessDisplayProps> = ({ invite, onReset }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = (text: string, label: string) => {
    void navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  const inviteLink = `${window.location.origin}/register?token=${invite.token}`;

  return (
    <Paper sx={{ p: 4, textAlign: "center" }}>
      <CheckCircle size={64} color="#4caf50" />
      <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>
        Invite Sent Successfully!
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        A tenant admin invitation has been sent to{" "}
        <strong>{invite.email}</strong>
      </Typography>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ textAlign: "left" }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Invite Details
        </Typography>

        <Box sx={{ display: "grid", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              <strong>Invite ID:</strong> {invite.id}
            </Typography>
            <IconButton
              size="small"
              onClick={() => {
                void copyToClipboard(invite.id, "ID");
              }}
              title="Copy ID"
            >
              <Copy size={16} />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              <strong>Status:</strong>{" "}
              <Chip size="small" label={invite.status} color="primary" />
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2">
              <strong>Expires:</strong>{" "}
              {new Date(invite.expiresAt).toLocaleString()}
            </Typography>
          </Box>

          <Divider />

          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Registration Link:</strong>
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 1.5,
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "grey.50",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {inviteLink}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  void copyToClipboard(inviteLink, "Link");
                }}
                title="Copy Link"
              >
                <Copy size={16} />
              </IconButton>
              <IconButton
                size="small"
                component={Link}
                href={inviteLink}
                target="_blank"
                title="Open Link"
              >
                <ExternalLink size={16} />
              </IconButton>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Button
        variant="contained"
        onClick={onReset}
        sx={{ mt: 4 }}
        startIcon={<Send />}
      >
        Send Another Invite
      </Button>

      <Snackbar
        open={!!copied}
        autoHideDuration={2000}
        onClose={() => setCopied(null)}
        message={`${copied} copied to clipboard`}
      />
    </Paper>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

const TenantAdminInvitePage: React.FC = () => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [successInvite, setSuccessInvite] = useState<InviteResponse | null>(
    null,
  );

  const handleUnlock = useCallback(() => {
    setIsUnlocked(true);
  }, []);

  const handleInviteSuccess = useCallback((response: InviteResponse) => {
    setSuccessInvite(response);
  }, []);

  const handleReset = useCallback(() => {
    setSuccessInvite(null);
  }, []);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "grey.100",
        py: 4,
        px: 2,
      }}
    >
      <Box sx={{ maxWidth: 700, mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
              <Building2 size={32} />
              Tenant Admin Invite Tool
            </Typography>
            <Typography color="text.secondary">
              Create and send tenant administrator invitations
            </Typography>
          </Box>

          {isUnlocked && (
            <Chip
              icon={<Shield size={16} />}
              label="Unlocked"
              color="success"
              variant="outlined"
            />
          )}
        </Box>

        {/* Main Content */}
        {!isUnlocked ? (
          <PasswordWall onUnlock={handleUnlock} />
        ) : successInvite ? (
          <SuccessDisplay invite={successInvite} onReset={handleReset} />
        ) : (
          <InviteForm onSuccess={handleInviteSuccess} />
        )}

        {/* Footer */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 4 }}
        >
          Prototype tool for testing. API: {CONFIG.API_BASE_URL}
        </Typography>
      </Box>
    </Box>
  );
};

export default TenantAdminInvitePage;
